import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { getStatusBadgeStyle, formatDate } from 'utils/lmmsHelpers';
import { SyutSelect } from 'components/ui';

// ============================================================================
// Types
// ============================================================================

export interface UserBuAccessFormData {
  userId: string;
  userName: string;
  entityId: string;
  businessUnitId: string;
  businessUnitIds: string[];
  role: string;
  isPrimary: boolean;
}

export interface UserBuAccessRecord {
  id: string;
  userId: string;
  userName: string;
  entityId: string;
  entityName: string;
  businessUnitId: string;
  buName: string;
  buCode: string;
  accessRole: string;
  isPrimary: boolean;
  isActive: boolean;
  grantedAt: string;
  grantedBy?: string;
  grantedByName?: string;
}

interface UserOption {
  value: string;
  label: string;
  role?: string;
}

interface EntityOption {
  value: string;
  label: string;
}

interface BranchOption {
  value: string;
  label: string;
  entityId: string;
}

interface UserBuAccessFormModalProps {
  isOpen: boolean;
  mode: 'assign' | 'edit';
  formData: UserBuAccessFormData;
  userOptions: UserOption[];
  entityOptions: EntityOption[];
  branchOptions: BranchOption[];
  onFormDataChange: (data: UserBuAccessFormData) => void;
  onSave: () => void;
  onClose: () => void;
  onRefreshUsers?: () => void;
  usersLoading?: boolean;
}

interface UserBuAccessViewModalProps {
  isOpen: boolean;
  record: UserBuAccessRecord | null;
  onClose: () => void;
}

const getAccessRoleLabel = (role: string): string => {
  const labels: Record<string, string> = {
    BRANCH_USER: 'Branch User',
    APPROVER: 'Approver',
    ADMIN: 'Admin',
    SYSTEM: 'System',
  };
  return labels[role] || role;
};

// ============================================================================
// Form Modal (Assign / Edit)
// ============================================================================

export const UserBuAccessFormModal: React.FC<UserBuAccessFormModalProps> = ({
  isOpen,
  mode,
  formData,
  userOptions,
  entityOptions,
  branchOptions,
  onFormDataChange,
  onSave,
  onClose,
  onRefreshUsers,
  usersLoading,
}) => {
  // Filter branches by selected entity — must be before early return
  const filteredBranches = useMemo(() => {
    if (!formData.entityId) return [];
    return branchOptions.filter((b) => b.entityId === formData.entityId);
  }, [formData.entityId, branchOptions]);

  if (!isOpen) return null;

  const title = mode === 'assign' ? 'Assign Branch Access' : 'Edit Branch Access';

  const handleChange = (field: keyof UserBuAccessFormData, value: string | boolean | string[]) => {
    const updated = { ...formData, [field]: value };

    // When user changes, update userName and role
    if (field === 'userId') {
      const selectedUser = userOptions.find((u) => u.value === value);
      updated.userName = selectedUser?.label || '';
      updated.role = selectedUser?.role || '';
    }

    // When entity changes, reset branch selections
    if (field === 'entityId') {
      updated.businessUnitId = '';
      updated.businessUnitIds = [];
    }

    onFormDataChange(updated);
  };

  const isValid = mode === 'assign'
    ? formData.userId.trim() !== '' &&
      formData.entityId.trim() !== '' &&
      formData.businessUnitIds.length > 0
    : formData.userId.trim() !== '' &&
      formData.entityId.trim() !== '' &&
      formData.businessUnitId.trim() !== '';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {mode === 'assign' ? (
            <>
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <label className="form-label" style={{ margin: 0 }}>User *</label>
                  {onRefreshUsers && (
                    <button
                      type="button"
                      onClick={onRefreshUsers}
                      disabled={usersLoading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: usersLoading ? 'not-allowed' : 'pointer',
                        padding: '2px',
                        color: 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.75rem',
                      }}
                      title="Refresh users list"
                    >
                      <IconSystem.refresh size={14} style={{ animation: usersLoading ? 'spin 1s linear infinite' : 'none' }} />
                      Refresh
                    </button>
                  )}
                </div>
                <SyutSelect
                  options={userOptions}
                  value={formData.userId}
                  onChange={(val: string | string[] | null) => handleChange('userId', (val as string) || '')}
                  placeholder="Select user..."
                  isClearable={false}
                  isSearchable={true}
                  size="sm"
                />
              </div>

              <div className="form-group">
                <SyutSelect
                  label="Entity (Company) *"
                  options={entityOptions}
                  value={formData.entityId}
                  onChange={(val: string | string[] | null) => handleChange('entityId', (val as string) || '')}
                  placeholder="Select entity..."
                  isClearable={false}
                  isSearchable={true}
                  size="sm"
                />
              </div>

              <div className="form-group">
                <SyutSelect
                  label="Branch(es) *"
                  options={filteredBranches}
                  value={formData.businessUnitIds}
                  onChange={(val: string | string[] | null) => handleChange('businessUnitIds', (val as string[]) || [])}
                  placeholder={formData.entityId ? 'Select one or more branches...' : 'Select entity first...'}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={true}
                  size="sm"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">User</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontSize: '0.85rem' }}>
                  {formData.userName}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Access Role</label>
            <div style={{
              padding: '0.5rem',
              background: 'var(--bg-secondary)',
              borderRadius: '0.375rem',
              fontSize: '0.85rem',
              color: formData.role ? 'var(--text-primary)' : 'var(--text-muted)',
            }}>
              {formData.role ? getAccessRoleLabel(formData.role) : 'Auto-filled when user is selected'}
            </div>
          </div>

          {mode === 'assign' && formData.businessUnitIds.length > 1 ? (
            <div className="form-group">
              <SyutSelect
                label="Primary Branch"
                options={filteredBranches.filter((b) => formData.businessUnitIds.includes(b.value))}
                value={formData.businessUnitId}
                onChange={(val: string | string[] | null) => handleChange('businessUnitId', (val as string) || '')}
                placeholder="Select primary branch (optional)..."
                isClearable={true}
                isSearchable={true}
                size="sm"
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                The user will be auto-directed to this branch on login
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={formData.isPrimary}
                  onChange={(e) => handleChange('isPrimary', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span>Primary Branch</span>
              </label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                The user will be auto-directed to this branch on login
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValid}>
            <IconSystem.save size={16} />
            {mode === 'assign'
              ? `Assign ${formData.businessUnitIds.length > 1 ? `${formData.businessUnitIds.length} Branches` : 'Access'}`
              : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

export const UserBuAccessViewModal: React.FC<UserBuAccessViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Access Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">User</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.userName}
              </div>
            </div>
            <div>
              <label className="form-label">Entity (Company)</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityName}
              </div>
            </div>
            <div>
              <label className="form-label">Branch</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.buName} ({record.buCode})
              </div>
            </div>
            <div>
              <label className="form-label">Access Role</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {getAccessRoleLabel(record.accessRole)}
              </div>
            </div>
            <div>
              <label className="form-label">Primary Branch</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.isPrimary ? 'ACTIVE' : 'INACTIVE')}>
                  {record.isPrimary ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.isActive ? 'ACTIVE' : 'INACTIVE')}>
                  {record.isActive ? 'Active' : 'Revoked'}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">Granted By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.grantedByName || '—'}
              </div>
            </div>
            <div>
              <label className="form-label">Granted At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.grantedAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
